/**
 * Host-side keyboard/mouse buffer for realtime script preview.
 *
 * Pushed into Wasm via `setScriptInputState` before each tick; edge sets are
 * cleared after the tick so `wasPressed` / `wasReleased` last one frame.
 */

export interface ScriptPreviewInputSnapshot {
  keysDown: string[];
  keysPressed: string[];
  keysReleased: string[];
  mouseX: number;
  mouseY: number;
  mouseInside: boolean;
  buttonsDown: number;
  buttonsPressed: number;
  buttonsReleased: number;
}

const CODE_TO_ALIAS: Record<string, string> = {
  Space: "space",
  Enter: "enter",
  NumpadEnter: "enter",
  Escape: "escape",
  Tab: "tab",
  ShiftLeft: "shift",
  ShiftRight: "shift",
  ControlLeft: "ctrl",
  ControlRight: "ctrl",
  AltLeft: "alt",
  AltRight: "alt",
  MetaLeft: "meta",
  MetaRight: "meta",
  ArrowLeft: "left",
  ArrowRight: "right",
  ArrowUp: "up",
  ArrowDown: "down",
};

/** Normalize KeyboardEvent.code / key into the Luau alias set. */
export function aliasFromKeyboardEvent(event: KeyboardEvent): string | undefined {
  const fromCode = CODE_TO_ALIAS[event.code];
  if (fromCode) {
    return fromCode;
  }
  if (/^Key[A-Z]$/.test(event.code)) {
    return event.code.slice(3).toLowerCase();
  }
  if (/^Digit[0-9]$/.test(event.code)) {
    return event.code.slice(5);
  }
  if (/^Numpad[0-9]$/.test(event.code)) {
    return event.code.slice(6);
  }
  const key = event.key.toLowerCase();
  if (key.length === 1 && /[a-z0-9]/.test(key)) {
    return key;
  }
  return undefined;
}

export class ScriptPreviewInputBuffer {
  private readonly keysDown = new Set<string>();
  private readonly keysPressed = new Set<string>();
  private readonly keysReleased = new Set<string>();
  mouseX = 0;
  mouseY = 0;
  mouseInside = false;
  buttonsDown = 0;
  private buttonsPressed = 0;
  private buttonsReleased = 0;
  private frameWidth = 1;
  private frameHeight = 1;

  setFrameSize(width: number, height: number): void {
    this.frameWidth = Math.max(1, width);
    this.frameHeight = Math.max(1, height);
  }

  noteKeyDown(event: KeyboardEvent): void {
    const alias = aliasFromKeyboardEvent(event);
    if (!alias) {
      return;
    }
    if (!this.keysDown.has(alias)) {
      this.keysPressed.add(alias);
    }
    this.keysDown.add(alias);
  }

  noteKeyUp(event: KeyboardEvent): void {
    const alias = aliasFromKeyboardEvent(event);
    if (!alias) {
      return;
    }
    if (this.keysDown.has(alias)) {
      this.keysReleased.add(alias);
    }
    this.keysDown.delete(alias);
  }

  notePointerMove(clientX: number, clientY: number, canvas: HTMLCanvasElement): void {
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      this.mouseInside = false;
      return;
    }
    const u = (clientX - rect.left) / rect.width;
    const v = (clientY - rect.top) / rect.height;
    this.mouseInside = u >= 0 && u <= 1 && v >= 0 && v <= 1;
    this.mouseX = Math.min(this.frameWidth, Math.max(0, u * this.frameWidth));
    this.mouseY = Math.min(this.frameHeight, Math.max(0, v * this.frameHeight));
  }

  notePointerButtons(buttons: number): void {
    const previous = this.buttonsDown;
    this.buttonsDown = buttons & 0xff;
    this.buttonsPressed |= this.buttonsDown & ~previous;
    this.buttonsReleased |= previous & ~this.buttonsDown;
  }

  notePointerLeave(): void {
    this.buttonsReleased |= this.buttonsDown;
    this.buttonsDown = 0;
    this.mouseInside = false;
  }

  clearEdges(): void {
    this.keysPressed.clear();
    this.keysReleased.clear();
    this.buttonsPressed = 0;
    this.buttonsReleased = 0;
  }

  reset(): void {
    this.keysDown.clear();
    this.keysPressed.clear();
    this.keysReleased.clear();
    this.mouseX = 0;
    this.mouseY = 0;
    this.mouseInside = false;
    this.buttonsDown = 0;
    this.buttonsPressed = 0;
    this.buttonsReleased = 0;
  }

  toSnapshot(): ScriptPreviewInputSnapshot {
    return {
      keysDown: [...this.keysDown],
      keysPressed: [...this.keysPressed],
      keysReleased: [...this.keysReleased],
      mouseX: this.mouseX,
      mouseY: this.mouseY,
      mouseInside: this.mouseInside,
      buttonsDown: this.buttonsDown,
      buttonsPressed: this.buttonsPressed,
      buttonsReleased: this.buttonsReleased,
    };
  }

  toJson(): string {
    return JSON.stringify(this.toSnapshot());
  }
}
